"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { ScoreResults } from "@/components/tools/stats-descriptive-more/score-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import { formatStat } from "@/lib/tool-kit/stats-descriptive/format";
import {
  type ConversionError,
  convertScore,
  isConversion,
  type ScaleId,
} from "@/lib/tool-kit/stats-descriptive-more/standard-scores";

/** English strings. A translated content file overrides these through `ui`. */
export const STANDARD_SCORE_CONVERTER_STRINGS = {
  fromLabel: "Convert from",
  scaleZ: "z-score (mean 0, SD 1)",
  scaleT: "T-score (mean 50, SD 10)",
  scaleIq: "IQ-style deviation score (mean 100, SD 15)",
  scaleNce: "NCE, normal curve equivalent (mean 50, SD 21.06)",
  scaleScaled: "Scaled score (mean 10, SD 3)",
  scaleCeeb: "College Board / CEEB scale (mean 500, SD 100)",
  scalePercentile: "Percentile rank (above 0, below 100)",
  scaleStanine: "Stanine (whole number, 1–9)",
  scaleSten: "Sten (whole number, 1–10)",
  scaleRaw: "Raw score (enter the mean and SD)",
  valueLabel: "Score",
  valuePlaceholder: "115",
  meanLabel: "Mean of the scores",
  sdLabel: "Standard deviation of the scores",
  tryExample: "Try an example",
  tryRaw: "Try a raw score",
  clear: "Clear",
  enterPrompt: "Enter a score to see it on every scale.",
  errorNotANumber:
    "Enter a number for the score (and the mean and SD for a raw score).",
  errorPercentile:
    "A percentile rank must be greater than 0 and less than 100.",
  errorStanine: "A stanine is a whole number from 1 to 9.",
  errorSten: "A sten is a whole number from 1 to 10.",
  errorSd: "The standard deviation must be greater than zero.",
  results: "Results",
  cardZ: "z-score",
  cardPercentile: "Percentile rank",
  cardT: "T-score",
  cardIq: "IQ-style score",
  cardNce: "NCE",
  cardStanine: "Stanine",
  bandNotice:
    "A {scale} of {value} stands for a whole band, not one point: z-scores {zRange}, percentile ranks {pRange}. The values below use the middle of the band, z = {z}.",
  bandStanine: "stanine",
  bandSten: "sten",
  tableTitle: "Every scale side by side",
  colScale: "Scale",
  colMean: "Mean",
  colSd: "SD",
  colScore: "Score",
  rowZ: "z-score",
  rowT: "T-score",
  rowIq: "IQ-style score",
  rowNce: "Normal curve equivalent (NCE)",
  rowScaled: "Scaled score",
  rowCeeb: "College Board (CEEB)",
  rowStanine: "Stanine",
  rowSten: "Sten",
  rowPercentile: "Percentile rank",
  percentSuffix: "{value}%",
  curveTitle: "Where this score falls",
  curveLabel:
    "The normal curve with the region below z = {z} shaded, which is {percentile} percent of scores.",
  curveMarker: "z = {z}",
  axisZ: "z",
  axisT: "T",
  axisIq: "IQ",
  stanineTitle: "The nine stanines",
  colStanine: "Stanine",
  colZRange: "z-score range",
  colPercentileRange: "Percentile ranks",
  colShare: "Share of scores",
  rangeTemplate: "{from} to {to}",
  openLow: "below {to}",
  openHigh: "{from} and above",
  workingTitle: "How the conversion was calculated",
  workRaw: "z = (x − mean) ÷ SD = ({value} − {mean}) ÷ {sd} = {z}",
  workLinear: "z = (x − mean) ÷ SD = ({value} − {mean}) ÷ {sd} = {z}",
  workPercentile: "z = Φ⁻¹(percentile ÷ 100) = Φ⁻¹({value} ÷ 100) = {z}",
  workBanded:
    "Middle of the band: z = (score − {centre}) ÷ 2 = ({value} − {centre}) ÷ 2 = {z}",
  workPercentileOut: "Percentile rank = 100 × Φ(z) = 100 × Φ({z}) = {value}",
  workT: "T = 50 + 10 × z = 50 + 10 × {z} = {value}",
  workIq: "IQ = 100 + 15 × z = 100 + 15 × {z} = {value}",
  workNce: "NCE = 50 + 21.06 × z = 50 + 21.06 × {z} = {value}",
  workScaled: "Scaled = 10 + 3 × z = 10 + 3 × {z} = {value}",
  workCeeb: "CEEB = 500 + 100 × z = 500 + 100 × {z} = {value}",
  workStanine:
    "Stanine = round(2 × z + 5) = round({raw}) → {value}, kept within 1 to 9",
  workSten:
    "Sten = round(2 × z + 5.5) = round({raw}) → {value}, kept within 1 to 10",
  workNormal:
    "Percentile ranks and the stanine and sten bands assume the scores are normally distributed.",
  copySummary: "Copy results",
  copied: "Copied!",
  summaryText:
    "z = {z}; percentile = {percentile}; T = {t}; IQ (15) = {iq}; NCE = {nce}; scaled = {scaled}; CEEB = {ceeb}; stanine = {stanine}; sten = {sten}",
} as const;

type Strings = typeof STANDARD_SCORE_CONVERTER_STRINGS;

function errorText(error: ConversionError, strings: Strings): string {
  const messages: Record<ConversionError, string> = {
    notANumber: strings.errorNotANumber,
    percentileRange: strings.errorPercentile,
    stanineRange: strings.errorStanine,
    stenRange: strings.errorSten,
    sdNotPositive: strings.errorSd,
  };
  return messages[error];
}

export default function StandardScoreConverterWidget() {
  const strings = useToolUi(STANDARD_SCORE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<ScaleId>("iq");
  const [valueText, setValueText] = useState("");
  const [meanText, setMeanText] = useState("");
  const [sdText, setSdText] = useState("");

  const value = parseNumberInput(valueText);
  const isRaw = scale === "raw";
  const result =
    value === null
      ? null
      : convertScore({
          scale,
          value,
          mean: parseNumberInput(meanText) ?? undefined,
          sd: parseNumberInput(sdText) ?? undefined,
        });
  const typed =
    valueText.trim() !== "" || (isRaw && (meanText !== "" || sdText !== ""));
  const conversion = result !== null && isConversion(result) ? result : null;
  const error = result !== null && !isConversion(result) ? result : null;
  const needsNumber = value === null && valueText.trim() !== "";
  const num = (n: number) => formatStat(fmt, n);
  const view = { strings, fmt, num };

  return (
    <>
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ss-scale"
            label={strings.fromLabel}
            onChange={setScale}
            options={[
              { value: "z", label: strings.scaleZ },
              { value: "t", label: strings.scaleT },
              { value: "iq", label: strings.scaleIq },
              { value: "nce", label: strings.scaleNce },
              { value: "scaled", label: strings.scaleScaled },
              { value: "ceeb", label: strings.scaleCeeb },
              { value: "percentile", label: strings.scalePercentile },
              { value: "stanine", label: strings.scaleStanine },
              { value: "sten", label: strings.scaleSten },
              { value: "raw", label: strings.scaleRaw },
            ]}
            value={scale}
          />
          <TextField
            id="ss-value"
            label={strings.valueLabel}
            numeric
            onChange={setValueText}
            placeholder={strings.valuePlaceholder}
            value={valueText}
          />
        </div>
        {isRaw ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ss-mean"
              label={strings.meanLabel}
              numeric
              onChange={setMeanText}
              value={meanText}
            />
            <TextField
              id="ss-sd"
              label={strings.sdLabel}
              numeric
              onChange={setSdText}
              value={sdText}
            />
          </div>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setScale("iq");
              setValueText("115");
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setScale("raw");
              setValueText("82");
              setMeanText("70");
              setSdText("8");
            }}
            type="button"
          >
            {strings.tryRaw}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!typed}
            onClick={() => {
              setValueText("");
              setMeanText("");
              setSdText("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {needsNumber ? (
        <ErrorNotice>{strings.errorNotANumber}</ErrorNotice>
      ) : null}
      {error && !needsNumber ? (
        <ErrorNotice>{errorText(error, strings)}</ErrorNotice>
      ) : null}
      {conversion ? (
        <ScoreResults
          conversion={conversion}
          input={{
            scale,
            value: value ?? 0,
            mean: parseNumberInput(meanText),
            sd: parseNumberInput(sdText),
          }}
          view={view}
        />
      ) : null}
      {conversion || error || needsNumber ? null : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
